import { useState, useEffect, useMemo } from "react"

import isBrowser from "@hfc/shared/utils/isBrowser"
import useWindowSize from "./useWindowSize"

import {
	Viewport,
	ViewportByViewportType,
} from "@hfc/shared/typings/layout/Viewport"

const ssrViewportByViewportType: ViewportByViewportType = {
	// OnePlus Nord 2 5G (5.5" / 1080p)
	mobile: { type: "mobile", widthPx: 2400, heightPx: 1080 }, // TODO: convert to 4:3 ratio
	// iPad Pro 3rd generation fall '21 (10.5" / 2160p)
	tablet: { type: "tablet", widthPx: 2160, heightPx: 1620 }, // TODO: convert to 4:3 ratio
	//  (4:3" / 1080p)
	desktop: { type: "desktop", widthPx: 1440, heightPx: 1080 },
} as const

const offsetThresholdPercentageMultiplier = 0.01

const isThresholdExceeded = (prevDimension: number, newDimension: number) => {
	const thresholdFloorPx =
		prevDimension * (1 - offsetThresholdPercentageMultiplier)
	const thresholdCeilPx =
		prevDimension * (1 + offsetThresholdPercentageMultiplier)

	const isThresholdExceeded =
		newDimension < thresholdFloorPx || newDimension > thresholdCeilPx

	return isThresholdExceeded
}

// TODO: save reference in game context? (performance?)
// TODO: get aspect ratio of svg (from viewport?) -> use svg library
const useViewport = (
	fallbackViewportType: Viewport["type"] = "mobile",
): Viewport => {
	const { innerWidth, innerHeight } = useWindowSize(100)
	const [viewportType, setViewportType] = useState(fallbackViewportType)
	const ssrViewport = ssrViewportByViewportType[fallbackViewportType]

	const [widthPx, setWidth] = useState(
		isBrowser() ? window.innerWidth : ssrViewport.widthPx,
	)
	const [heightPx, setHeight] = useState(
		isBrowser() ? window.innerHeight : ssrViewport.heightPx,
	)
	const ratio = useMemo(() => heightPx / widthPx, [heightPx, widthPx])

	useEffect(() => {
		const handleResize = () => {
			if (
				isThresholdExceeded(widthPx, innerWidth) ||
				isThresholdExceeded(heightPx, innerHeight)
			) {
				console.info(
					"Updating viewport...",
					[widthPx, heightPx],
					[innerWidth, innerHeight],
				)

				setWidth(innerWidth)
				setHeight(innerHeight)

				// TODO: update viewport type
				setViewportType("mobile")
			}
		}

		handleResize()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [innerWidth, innerHeight])

	return { type: viewportType, widthPx, heightPx, ratio }
}

export default useViewport
